import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TaskItem, TaskList } from '../../../types/tasks';
import { useTasks } from '../../../context/TasksContext';
import { PROJECT_MAX_DEPTH, childrenOf, descendantIds } from '../../../utils/taskTree';
import TaskDialog from '../../tasks/TaskDialog';
import TaskIcon from '../../tasks/TaskIcon';
import MapNode, { CommitNext, DropZone } from './MapNode';
import { MapMode, ROOT_KEY, Size, connectorPath, keyOf, layoutMap } from './layout';
import { usePanZoom } from './usePanZoom';
import { projectProgress } from '../projectStats';
import { Folding } from '../useCollapsed';
import FoldButtons from '../FoldButtons';

interface MindMapProps {
  project: TaskList;
  items: TaskItem[];
  readOnly: boolean;
  mode: MapMode;
  /** Select, reveal and pulse this item (deep link). */
  focusItemId: number | null;
  /** Folded branches, shared with the Outline and Gantt chart. */
  fold: Folding;
}

const DRAG_THRESHOLD = 5;

function estimate(text: string, root: boolean): Size {
  return root ? { w: Math.min(300, 160 + text.length * 9), h: 52 } : { w: Math.min(300, Math.max(180, 96 + text.length * 7)), h: 40 };
}

interface DragState {
  key: string;
  pointerId: number;
  startX: number;
  startY: number;
  dx: number;
  dy: number;
  active: boolean;
  target: { key: string; zone: DropZone } | null;
}

/**
 * A project as an Xmind-style mind map. The central topic is the project; its
 * top-level items are the main branches. Keyboard works like Xmind (and like
 * Lists): Tab adds a sub-topic, Enter a sibling, F2 / Space or just typing
 * edits, Delete removes, arrows move around; drag a topic onto another to
 * move it there.
 */
const MindMap: React.FC<MindMapProps> = ({ project, items, readOnly, mode, focusItemId, fold }) => {
  const { createItem, updateItem, deleteItem, moveItems, updateList } = useTasks();
  const viewportRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { view, zoomAt, centerOn, fit, home, reveal, backgroundHandlers } = usePanZoom(viewportRef);

  // ── The canvas reaches to the bottom of the window ─────────────────────────
  // Whatever is under it (the key hint, the page's own padding) stays on
  // screen; whatever is above it (header, an error bar) moves it down.
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const map = viewportRef.current;
    const wrap = wrapRef.current;
    if (!map || !wrap) return;
    const paddingBelow = (el: Element | null) => (el ? parseFloat(getComputedStyle(el).paddingBottom) || 0 : 0);
    const size = () => {
      const top = map.getBoundingClientRect().top + window.scrollY;
      const under = wrap.getBoundingClientRect().bottom - map.getBoundingClientRect().bottom;
      const below = under + paddingBelow(wrap.closest('.tk-page')) + paddingBelow(wrap.closest('.main-content'));
      const next = Math.max(340, Math.floor(window.innerHeight - top - below));
      setHeight((prev) => (prev === next ? prev : next));
    };
    size();
    window.addEventListener('resize', size);
    const page = wrap.closest('.tk-page');
    const ro = page && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(size) : null;
    // The page itself too: something new above the map (an error bar) grows it.
    if (page && ro) [page, ...Array.from(page.children).filter((c) => !c.contains(wrap))].forEach((c) => ro.observe(c));
    return () => {
      window.removeEventListener('resize', size);
      ro?.disconnect();
    };
  }, []);

  const kids = useMemo(() => childrenOf(items), [items]);
  const byKey = useMemo(() => new Map(items.map((i) => [keyOf(i), i])), [items]);

  // ── Folding (per person, per project; shared with the Gantt chart) ────────
  const { collapsed, toggle: toggleCollapse, expandTo } = fold;

  // ── Measured sizes → layout ────────────────────────────────────────────────
  const [sizes, setSizes] = useState<Map<string, Size>>(new Map());
  const elements = useRef(new Map<string, HTMLDivElement>());
  const pendingMeasure = useRef<number | null>(null);
  const measure = useCallback(() => {
    if (pendingMeasure.current !== null) return;
    pendingMeasure.current = requestAnimationFrame(() => {
      pendingMeasure.current = null;
      setSizes((prev) => {
        let changed = false;
        const next = new Map(prev);
        elements.current.forEach((el, key) => {
          const w = el.offsetWidth;
          const h = el.offsetHeight;
          const old = prev.get(key);
          if (!old || old.w !== w || old.h !== h) {
            next.set(key, { w, h });
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    });
  }, []);
  const observer = useMemo(
    () => (typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null),
    [measure]
  );
  useEffect(() => () => observer?.disconnect(), [observer]);
  // One stable ref callback per topic, so re-renders don't re-observe.
  const refCallbacks = useRef(new Map<string, (el: HTMLDivElement | null) => void>());
  const nodeRef = (key: string) => {
    let cb = refCallbacks.current.get(key);
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        const old = elements.current.get(key);
        if (old && old !== el) observer?.unobserve(old);
        if (el) {
          elements.current.set(key, el);
          observer?.observe(el);
        } else {
          elements.current.delete(key);
          refCallbacks.current.delete(key);
        }
      };
      refCallbacks.current.set(key, cb);
    }
    return cb;
  };
  useLayoutEffect(measure);

  const layout = useMemo(
    () =>
      layoutMap({
        kids,
        collapsed,
        mode,
        sizeOf: (key) =>
          sizes.get(key) || (key === ROOT_KEY ? estimate(project.title, true) : estimate(byKey.get(key)?.text || '', false)),
      }),
    [kids, collapsed, mode, sizes, byKey, project.title]
  );
  const measured = sizes.has(ROOT_KEY);

  // ── Selection & editing ────────────────────────────────────────────────────
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TaskItem | null>(null);

  // The selected topic stays in view as you move around with the keyboard.
  useEffect(() => {
    const box = selected ? layout.boxes.get(selected) : undefined;
    if (box) reveal(box);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const focusMap = () => viewportRef.current?.focus({ preventScroll: true });

  const startEdit = (key: string, text?: string) => {
    if (readOnly) return;
    const item = byKey.get(key);
    setSelected(key);
    setEditing({ key, text: text ?? (key === ROOT_KEY ? project.title : item?.text || '') });
  };

  const addChild = (parent: TaskItem | null) => {
    if (readOnly || (parent && parent.depth >= PROJECT_MAX_DEPTH)) return;
    if (parent && collapsed.has(parent.id)) toggleCollapse(parent);
    const siblings = kids.get(parent ? parent.id : null) || [];
    const last = siblings[siblings.length - 1];
    const { key } = createItem({ list_id: project.id, parent_id: parent?.id ?? null, text: '', sort_order: last ? last.sort_order + 1 : 1 });
    setSelected(key);
    setEditing({ key, text: '' });
  };

  const addSibling = (item: TaskItem) => {
    if (readOnly) return;
    const siblings = kids.get(item.parent_id ?? null) || [];
    const at = siblings.findIndex((s) => s.id === item.id);
    const next = siblings[at + 1];
    const sortOrder = next ? (item.sort_order + next.sort_order) / 2 : item.sort_order + 1;
    const { key } = createItem({ list_id: project.id, parent_id: item.parent_id, text: '', sort_order: sortOrder });
    setSelected(key);
    setEditing({ key, text: '' });
  };

  const parentKeyOf = (item: TaskItem) => {
    const parent = item.parent_id ? items.find((i) => i.id === item.parent_id) : undefined;
    return parent ? keyOf(parent) : ROOT_KEY;
  };

  const commit = (key: string, text: string, next: CommitNext) => {
    setEditing(null);
    focusMap();
    const value = text.trim();
    if (key === ROOT_KEY) {
      if (value !== project.title) updateList(project.id, { title: value });
      if (next !== 'none') addChild(null);
      return;
    }
    const item = byKey.get(key);
    if (!item) return;
    if (!value) {
      // A topic left blank goes away, as an emptied line does on a list.
      if (!(kids.get(item.id) || []).length && !item.note) {
        deleteItem(item.id);
        setSelected(parentKeyOf(item));
      }
      return;
    }
    if (value !== item.text) updateItem(item.id, { text: value });
    if (next === 'sibling') addSibling(item);
    if (next === 'child') addChild(item);
  };

  const remove = (item: TaskItem) => {
    if (readOnly) return;
    if ((kids.get(item.id) || []).length) {
      setConfirmDelete(item);
      return;
    }
    const siblings = kids.get(item.parent_id ?? null) || [];
    const at = siblings.findIndex((s) => s.id === item.id);
    const neighbour = siblings[at + 1] || siblings[at - 1];
    setSelected(neighbour ? keyOf(neighbour) : parentKeyOf(item));
    deleteItem(item.id);
  };

  /** Ctrl/⌘ + ↑/↓: swap with the sibling above or below. */
  const reorder = (item: TaskItem, dir: -1 | 1) => {
    const siblings = kids.get(item.parent_id ?? null) || [];
    const at = siblings.findIndex((s) => s.id === item.id);
    const other = siblings[at + dir];
    if (!other || item.id < 0 || other.id < 0) return;
    moveItems(project.id, [
      { id: item.id, parent_id: item.parent_id, depth: item.depth, sort_order: other.sort_order },
      { id: other.id, parent_id: other.parent_id, depth: other.depth, sort_order: item.sort_order },
    ]);
  };

  // ── Keyboard ───────────────────────────────────────────────────────────────
  const neighbour = (key: string, dir: 'up' | 'down' | 'left' | 'right'): string | null => {
    const box = layout.boxes.get(key);
    if (!box) return null;
    const childrenOnSide = (parentKey: string, side: 1 | -1) =>
      Array.from(layout.boxes.values()).filter((b) => b.parentKey === parentKey && b.side === side);
    if (dir === 'left' || dir === 'right') {
      const side = dir === 'right' ? 1 : -1;
      if (key === ROOT_KEY) return childrenOnSide(ROOT_KEY, side)[0]?.key || null;
      if (box.side === side) {
        if (box.item && collapsed.has(box.item.id)) {
          toggleCollapse(box.item);
          return null;
        }
        return childrenOnSide(key, side)[0]?.key || null;
      }
      return box.parentKey;
    }
    if (!box.parentKey) return null;
    const siblings = childrenOnSide(box.parentKey, box.side);
    const at = siblings.findIndex((b) => b.key === key);
    return siblings[at + (dir === 'down' ? 1 : -1)]?.key || null;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (editing || target.closest('textarea, input, .tk-popover')) return;
    // Space / Enter on a focused button (a tick, a fold) press that button.
    if ((e.key === ' ' || e.key === 'Enter') && target.closest('button')) return;
    const key = selected;
    const item = key ? byKey.get(key) : undefined;
    const mod = e.ctrlKey || e.metaKey;

    if (e.key === 'Tab') {
      e.preventDefault();
      if (key === ROOT_KEY || !key) addChild(null);
      else if (item) addChild(item);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (key === ROOT_KEY || !key) addChild(null);
      else if (item) addSibling(item);
    } else if ((e.key === 'F2' || e.key === ' ') && key) {
      e.preventDefault();
      startEdit(key);
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && item) {
      e.preventDefault();
      remove(item);
    } else if (e.key.startsWith('Arrow')) {
      e.preventDefault();
      if (!key) {
        setSelected(ROOT_KEY);
        return;
      }
      const dir = e.key.slice(5).toLowerCase() as 'up' | 'down' | 'left' | 'right';
      if (mod && item && (dir === 'up' || dir === 'down')) {
        reorder(item, dir === 'up' ? -1 : 1);
        return;
      }
      const next = neighbour(key, dir);
      if (next) setSelected(next);
    } else if (e.key === 'Escape') {
      setSelected(null);
    } else if (key && !mod && !e.altKey && e.key.length === 1) {
      // Typing on a selected topic starts replacing its text, as in Xmind.
      e.preventDefault();
      startEdit(key, e.key);
    }
  };

  // ── Drag a topic onto another ──────────────────────────────────────────────
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;

  const draggedIds = useMemo(() => {
    if (!drag) return new Set<number>();
    const item = byKey.get(drag.key);
    return item ? new Set([item.id, ...descendantIds(items, item.id)]) : new Set<number>();
  }, [drag, byKey, items]);

  const onNodePointerDown = (key: string) => (e: React.PointerEvent) => {
    // Never let it reach the canvas: a pan there captures the pointer, which
    // would also swallow clicks in this node's popovers (portalled, but their
    // React events still bubble through here).
    e.stopPropagation();
    if (!(e.currentTarget as HTMLElement).contains(e.target as Node)) return;
    focusMap();
    if (readOnly || key === ROOT_KEY || editing?.key === key) return;
    if ((e.target as HTMLElement).closest('button, textarea, input')) return;
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    setDrag({ key, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, dx: 0, dy: 0, active: false, target: null });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      const active = d.active || Math.hypot(dx, dy) > DRAG_THRESHOLD;
      let target: DragState['target'] = null;
      if (active) {
        const el = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest('[data-node-key]') as HTMLElement | null;
        const overKey = el?.dataset.nodeKey;
        const over = overKey && overKey !== ROOT_KEY ? byKey.get(overKey) : undefined;
        if (el && overKey && overKey !== d.key && !(over && draggedIds.has(over.id))) {
          const rect = el.getBoundingClientRect();
          const rel = (e.clientY - rect.top) / rect.height;
          const zone: DropZone = overKey === ROOT_KEY ? 'child' : rel < 0.28 ? 'before' : rel > 0.72 ? 'after' : 'child';
          target = { key: overKey, zone };
        }
      }
      setDrag({ ...d, dx, dy, active, target });
    };
    const onUp = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      setDrag(null);
      if (d.active && d.target) drop(d.key, d.target.key, d.target.zone);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.key, drag?.pointerId, byKey, draggedIds]);

  const drop = (key: string, targetKey: string, zone: DropZone) => {
    const item = byKey.get(key);
    if (!item || item.id < 0) return;
    const target = targetKey === ROOT_KEY ? null : byKey.get(targetKey) || null;
    let parentId: number | null;
    let sortOrder: number;
    if (zone === 'child' || !target) {
      parentId = target ? target.id : null;
      const siblings = (kids.get(parentId) || []).filter((s) => s.id !== item.id);
      sortOrder = siblings.length ? siblings[siblings.length - 1].sort_order + 1 : 1;
      if (target && collapsed.has(target.id)) toggleCollapse(target);
    } else {
      parentId = target.parent_id;
      const siblings = (kids.get(parentId) || []).filter((s) => s.id !== item.id);
      const at = siblings.findIndex((s) => s.id === target.id);
      const other = siblings[at + (zone === 'before' ? -1 : 1)];
      sortOrder = other ? (target.sort_order + other.sort_order) / 2 : target.sort_order + (zone === 'before' ? -1 : 1);
    }
    if (parentId !== null && parentId < 0) return;
    const parent = parentId ? items.find((i) => i.id === parentId) : undefined;
    const depth = parent ? parent.depth + 1 : 0;
    const below = Math.max(0, ...descendantIds(items, item.id).map((id) => (items.find((i) => i.id === id)?.depth ?? 0) - item.depth));
    if (depth + below > PROJECT_MAX_DEPTH) return;
    moveItems(project.id, [{ id: item.id, parent_id: parentId, depth, sort_order: sortOrder }]);
    setSelected(key);
  };

  // ── First view and deep links ──────────────────────────────────────────────
  // A right-growing map sits against the left edge, the project at the start.
  const fitMap = useCallback(
    () => fit(layout.bounds, mode === 'logic' ? 'left' : 'center', layout.boxes.get(ROOT_KEY)),
    [fit, layout.bounds, layout.boxes, mode]
  );
  const placed = useRef(false);
  useEffect(() => {
    if (!measured || placed.current) return;
    placed.current = true;
    fitMap();
  }, [measured, fitMap]);

  // Expand all / Collapse all reshape the map: frame it again once it's redrawn.
  const refitPending = useRef(false);
  useEffect(() => {
    if (!refitPending.current || !measured) return;
    refitPending.current = false;
    fitMap();
  }, [layout, measured, fitMap]);

  // Switching between Balanced and Right redraws the map, so frame it again.
  const lastMode = useRef(mode);
  useEffect(() => {
    if (lastMode.current === mode) return;
    lastMode.current = mode;
    if (measured) fitMap();
  }, [mode, measured, fitMap]);

  const handledFocus = useRef<number | null>(null);
  useEffect(() => {
    if (!focusItemId || handledFocus.current === focusItemId) return;
    const item = items.find((i) => i.id === focusItemId);
    if (!item) return;
    expandTo(item);
    const key = keyOf(item);
    const box = layout.boxes.get(key);
    if (!box || !measured) return;
    handledFocus.current = focusItemId;
    placed.current = true;
    setSelected(key);
    centerOn({ x: box.x + box.w / 2, y: box.y + box.h / 2 });
    setFlashKey(key);
  }, [focusItemId, items, layout, measured, expandTo, centerOn]);

  useEffect(() => {
    if (!flashKey) return;
    const timer = window.setTimeout(() => setFlashKey(null), 2600);
    return () => window.clearTimeout(timer);
  }, [flashKey]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const rootProgress = useMemo(() => projectProgress(items), [items]);

  const boxes = Array.from(layout.boxes.values());
  const pressedAt = useRef<{ x: number; y: number } | null>(null);

  return (
    <div ref={wrapRef} className="tp-map-wrap">
      <div
        ref={viewportRef}
        className={`tp-map${drag?.active ? ' is-dragging' : ''}`}
        style={height ? { height } : undefined}
        tabIndex={0}
        onKeyDown={onKeyDown}
        aria-label={`Mind map of ${project.title || 'this project'}`}
        {...backgroundHandlers}
        onPointerDown={(e) => {
          pressedAt.current = { x: e.clientX, y: e.clientY };
          backgroundHandlers.onPointerDown(e);
        }}
        onPointerUp={(e) => {
          backgroundHandlers.onPointerUp(e);
          const p = pressedAt.current;
          pressedAt.current = null;
          // A click on the background (not a pan) clears the selection.
          if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) < DRAG_THRESHOLD) setSelected(null);
        }}
      >
        <div
          className={`tp-stage${measured ? '' : ' is-measuring'}`}
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}
        >
          <svg className="tp-links" width="1" height="1" aria-hidden="true">
            {boxes.map((b) => {
              const parent = b.parentKey ? layout.boxes.get(b.parentKey) : undefined;
              if (!parent) return null;
              return (
                <path
                  key={b.key}
                  d={connectorPath(parent, b)}
                  className={`tp-link tp-link-l${Math.min(b.level, 3)}`}
                  style={{ stroke: `var(--tp-branch-${b.branch % 8})` }}
                />
              );
            })}
          </svg>
          {boxes.map((b) => {
            const item = b.item;
            const dragging = !!drag?.active && (b.key === drag.key || (!!item && draggedIds.has(item.id)));
            return (
              <MapNode
                key={b.key}
                box={b}
                title={b.key === ROOT_KEY ? project.title : undefined}
                rootProgress={rootProgress}
                selected={selected === b.key}
                editing={editing?.key === b.key ? editing.text : null}
                flash={flashKey === b.key}
                readOnly={readOnly}
                hasChildren={item ? (kids.get(item.id) || []).length > 0 : false}
                collapsed={!!item && collapsed.has(item.id)}
                dropZone={drag?.target?.key === b.key ? drag.target.zone : null}
                dragOffset={dragging ? { x: drag!.dx / view.k, y: drag!.dy / view.k } : null}
                nodeRef={nodeRef(b.key)}
                onSelect={() => setSelected(b.key)}
                onStartEdit={() => startEdit(b.key)}
                onCommit={(text, next) => commit(b.key, text, next)}
                onToggleCollapse={() => item && toggleCollapse(item)}
                onAddChild={() => addChild(item)}
                onPointerDown={onNodePointerDown(b.key)}
              />
            );
          })}
        </div>
      </div>

      <div className="tp-map-tools" role="group" aria-label="View">
        <FoldButtons fold={fold} onChange={() => (refitPending.current = true)} />
        <span className="tp-tools-sep" aria-hidden="true" />
        <button type="button" className="tk-icon-btn" onClick={() => zoomAt(view.k / 1.2)} aria-label="Zoom out" title="Zoom out">
          <TaskIcon name="zoom-out" size={16} />
        </button>
        <button
          type="button"
          className="tp-zoom-label"
          onClick={() => {
            const root = layout.boxes.get(ROOT_KEY);
            if (root) home(root, mode === 'logic' ? 'left' : 'center');
            else zoomAt(1);
          }}
          title="Back to 100%, centred on the project"
        >
          {Math.round(view.k * 100)}%
        </button>
        <button type="button" className="tk-icon-btn" onClick={() => zoomAt(view.k * 1.2)} aria-label="Zoom in" title="Zoom in">
          <TaskIcon name="zoom-in" size={16} />
        </button>
        <button type="button" className="tk-icon-btn" onClick={fitMap} aria-label="Recenter the map" title="Recenter: fit the whole map, or centre on the project if it's too big to fit">
          <TaskIcon name="fit" size={16} />
        </button>
      </div>

      {!readOnly && (
        <p className="tp-map-hint">
          <kbd>Tab</kbd> sub-topic · <kbd>Enter</kbd> sibling · <kbd>F2</kbd> edit · <kbd>Del</kbd> delete · drag a topic onto another to move it
        </p>
      )}

      {confirmDelete && (
        <TaskDialog
          title={`Delete "${confirmDelete.text || 'this topic'}"?`}
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button type="button" className="tk-btn tk-btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button
                type="button"
                className="tk-btn tk-btn-danger"
                onClick={() => {
                  setSelected(parentKeyOf(confirmDelete));
                  deleteItem(confirmDelete.id);
                  setConfirmDelete(null);
                  focusMap();
                }}
              >
                Delete
              </button>
            </>
          }
        >
          <p>
            It and the {descendantIds(items, confirmDelete.id).length} topics under it are removed for everyone on the
            project, along with any Lists cards made from them.
          </p>
        </TaskDialog>
      )}
    </div>
  );
};

export default MindMap;
